import { motion, useInView } from "framer-motion"
import { useRef, useState } from "react"
import { FaRegStar, FaStar } from "react-icons/fa"
import "swiper/css/bundle"
import { Autoplay } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import G2Icon from "../../images/svgs/g2icon.svg"
import { Button } from "../../myne/Button/Button"
import Flickity from "react-flickity-component"
import { StaticImage } from "gatsby-plugin-image"

const testimonials = [
  {
    quote:
      "I used Premagic for one of my recent events and was honestly blown away by how smooth and effective it was. The social media posters looked fantastic and made our event branding feel really professional without any extra hassle.",
    author: "Artem R",
    role: "Founder & Curator",
    star: 5,
  },
  {
    quote:
      "What I like best about Premagic is the convenience—it streamlines the process of collecting and sharing event photos, especially when there are a lot of attendees.",
    author: "Shane",
    role: "Events Manager",
    star: 5,
  },
  {
    quote:
      "Premagic has been a true game-changer for our B2B marketing. Branded content, sponsorship integrations, advocacy posters — so many valuable features.",
    author: "Lorraine F",
    role: "Marketing Manager",
    star: 4,
  },
  {
    quote:
      "I would definitely attribute a portion of our registrations to how easy it was for attendees to share the marketing posters socially.",
    author: "Kevin Fernandes",
    role: "G2 APAC",
    star: 4,
  },
]

const flickityOptions = {
  autoPlay: 4000,
  wrapAround: true,
  selectedAttraction: 0.01,
  cellAlign: "left",
  contain: true,
  pageDots: false,
  prevNextButtons: false,
  pauseAutoPlayOnHover: true,
}

const StarRow = ({ star = 5 }) => {
  const totalStars = 5

  return (
    <div className="flex gap-1 text-yellow-400 mb-6">
      {Array.from({ length: star }).map((_, i) => (
        <FaStar key={`filled-${i}`} size={16} />
      ))}

      {Array.from({ length: totalStars - star }).map((_, i) => (
        <FaRegStar key={`empty-${i}`} size={16} />
      ))}
    </div>
  )
}

const TestimonialCard = ({ item }) => (
  <div className="bg-white rounded-[16px] p-6 md:p-8 border border-[#E3E0F1] h-[320px] md:h-auto flex flex-col justify-between">
    <p className="text-gray-700 text-sm md:text-lg font-medium font-secondary leading-relaxed mb-6">
      "{item.quote}"
    </p>
    <div>
      <StarRow star={item.star} />
      <h4 className="font-bold text-gray-900">{item.author}</h4>
      <p className="text-sm text-gray-500">{item.role}</p>
    </div>
  </div>
)

export default function UserInsights() {
  return (
    <section className="py-10 md:pb-28 md:pt-0 pb-16 px-6 h-auto">
      <div className="container mx-auto flex flex-col lg:flex-row gap-6 items-center md:h-[700px]">
        <div className="w-full md:w-[570px] h-full">
          <div className="bg-gradient-to-br from-primary-soft via-[#876DF4] to-[#67AFF6] rounded-[30px] md:rounded-[40px] md:p-14 p-8 text-white h-full flex flex-col justify-between">
            <div>
              <h2 className="text-2xl md:text-5xl font-bold text-white !leading-[1.3] mb-6">
                User Insights: <br className="hidden md:block" />
                Real Stories, <br className="hidden md:block" /> Real Impact!
              </h2>
              <p className="text-white text-base md:text-lg max-w-md leading-relaxed mb-6 md:mb-0">
                Discover why industry leaders trust Premagic to amplify their
                event's reach and visibility.
              </p>
            </div>

            <div className="pt-10 md:pt-0">
              <p className="font-semibold text-white text-base md:text-lg max-w-[75%] md:max-w-[60%] mb-4">
                Best Use of Technology for Marketing an Event
              </p>
              <StaticImage
                src="../../images/client-logos/latest/event-tech-winner.png"
                alt="Event Tech"
                width={140}
              />
              <div className="flex items-center mt-10 gap-4 pt-8 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <img src={G2Icon} alt="G2" width={110} height={30} />
                  <span className="text-md opacity-80 block ml-2">4.5/5</span> |
                </div>
                <div className="flex gap-1 text-white">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0.4, filter: "blur(6px)", opacity: 0 }}
                      whileInView={{
                        scale: 1,
                        filter: "blur(0px)",
                        opacity: 1,
                      }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.4,
                        delay: i * 0.15,
                        ease: "easeOut",
                      }}
                    >
                      <FaStar size={16} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full md:w-[60%] h-full">
          <div className="hidden md:block h-full">
            <TestimonialVerticalSlider />
          </div>
          <div className="block md:hidden">
            <TestimonialHorizontalSlider />
          </div>
        </div>
      </div>
    </section>
  )
}

const TestimonialVerticalSlider = () => {
  const [isPaused, setIsPaused] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, margin: "0px 0px -100px 0px" })

  return (
    <div
      ref={ref}
      className="w-full h-full relative overflow-hidden rounded-[30px] md:rounded-[40px] bg-[#D9D0FF]/40 backdrop-blur-sm px-8 md:px-20"
    >
      <motion.div
        className="space-y-4 md:p-6"
        style={{ willChange: "transform" }}
        animate={isInView && !isPaused ? { y: ["0%", "-50%"] } : {}}
        transition={{ duration: 15, ease: "linear", repeat: Infinity }}
      >
        {[...testimonials, ...testimonials].map((item, idx) => (
          <div
            key={idx}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <TestimonialCard item={item} />
          </div>
        ))}
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-[#D9D0FF]/60 to-transparent pointer-events-none z-4" />
      <div className="absolute inset-x-0 bottom-0 z-[10] pointer-events-auto px-8 md:px-20 pb-6">
        <div className="flex justify-center">
          <Button to="/case-study" variant="primary">
            View Case Study
          </Button>
        </div>
      </div>
    </div>
  )
}

const TestimonialHorizontalSlider = () => (
  <div className="w-full h-fit relative overflow-hidden">
    <Flickity
      className="carousel py-6 border-none outline-none"
      elementType="div"
      options={flickityOptions}
      reloadOnUpdate
      static
    >
      {testimonials.map((item, idx) => (
        <div key={idx} className="w-full md:w-1/2 px-3">
          <div className="bg-white rounded-[16px] p-6 md:p-8 border border-[#E3E0F1] min-h-[350px] flex flex-col justify-between">
            <p className="text-gray-700 text-sm md:text-lg font-medium font-secondary leading-relaxed">
              "{item.quote}"
            </p>

            <div>
              <div className="flex gap-1 text-yellow-400 mb-6">
                <StarRow star={item.star} />
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <h4 className="font-bold text-gray-900">{item.author}</h4>
                  <p className="text-sm text-gray-500">{item.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Flickity>

    <div className="mt-10 flex justify-center">
      <Button to="/case-study" variant="primary">
        View Case Study
      </Button>
    </div>
  </div>
)
